/**
 * Utility for copying formatted content to clipboard
 * Preserves visual formatting (bold, colors, tables, etc.) without exposing HTML syntax
 */

/**
 * Copies content with formatting preserved (like copying from a PDF)
 * Works in modern browsers that support the Clipboard API
 */
export const copyFormattedContent = async (html: string): Promise<boolean> => {
  try {
    // Try modern Clipboard API first (best approach)
    if (navigator.clipboard && window.ClipboardItem) {
      const htmlBlob = new Blob([html], { type: 'text/html' });
      const textBlob = new Blob([htmlToPlainText(html)], { type: 'text/plain' });

      const clipboardItem = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob,
      });

      await navigator.clipboard.write([clipboardItem]);
      return true;
    }

    // Fallback to legacy approach for older browsers
    return copyFormattedContentLegacy(html);
  } catch (error) {
    console.error('Failed to copy formatted content:', error);
    // Fallback to legacy method
    return copyFormattedContentLegacy(html);
  }
};

/**
 * Legacy method for copying formatted content
 * Creates a temporary element and uses document.execCommand
 */
const copyFormattedContentLegacy = (html: string): boolean => {
  try {
    // Create a temporary container
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.contentEditable = 'true';
    container.innerHTML = html;

    document.body.appendChild(container);

    // Select the content
    const range = document.createRange();
    range.selectNodeContents(container);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);

      // Copy to clipboard
      const success = document.execCommand('copy');

      // Clean up
      selection.removeAllRanges();
      document.body.removeChild(container);

      return success;
    }

    document.body.removeChild(container);
    return false;
  } catch (error) {
    console.error('Legacy copy failed:', error);
    return false;
  }
};

/**
 * Converts HTML to clean plain text (for the text/plain clipboard format)
 */
const htmlToPlainText = (html: string): string => {
  if (!html) return '';

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Add line breaks before block elements
  const blockElements = tempDiv.querySelectorAll(
    'div, p, h1, h2, h3, h4, h5, h6, li, tr, br'
  );
  blockElements.forEach(el => {
    if (el.tagName === 'BR') {
      const textNode = document.createTextNode('\n');
      el.replaceWith(textNode);
    } else {
      const textNode = document.createTextNode('\n');
      el.parentNode?.insertBefore(textNode, el);
    }
  });

  // Add bullets for list items
  const listItems = tempDiv.querySelectorAll('li');
  listItems.forEach(li => {
    const bullet = document.createTextNode('• ');
    li.insertBefore(bullet, li.firstChild);
  });

  // Handle tables
  const tables = tempDiv.querySelectorAll('table');
  tables.forEach(table => {
    const rows = Array.from(table.querySelectorAll('tr'));
    let tableText = '\n';

    rows.forEach(row => {
      const cells = Array.from(row.querySelectorAll('th, td'));
      const rowText = cells.map(cell => cell.textContent?.trim() || '').join(' | ');
      tableText += rowText + '\n';
    });

    const textNode = document.createTextNode(tableText);
    table.replaceWith(textNode);
  });

  let text = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up excessive whitespace
  text = text
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    .replace(/[ \t]+/g, ' ')
    .trim();

  return text;
};

/**
 * Simple fallback for plain text only copy
 */
export const copyPlainText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback to execCommand
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch (error) {
    console.error('Failed to copy plain text:', error);
    return false;
  }
};
